import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { downloadPhotoUrl, eventPhotoUrl } from '../utils/imageUrls';

const SWIPE_THRESHOLD = 60;

function ResultsGrid({ results }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const touchStartXRef = useRef(0);
  const touchDeltaXRef = useRef(0);

  const imageUrls = useMemo(
    () => results.map((result) => eventPhotoUrl(result.img)),
    [results]
  );
  const downloadUrls = useMemo(
    () => results.map((result) => downloadPhotoUrl(result.img)),
    [results]
  );

  const total = imageUrls.length;
  const isAtStart = currentIndex === 0;
  const isAtEnd = currentIndex === Math.max(0, total - 1);
  const mainUrl = imageUrls[currentIndex];
  const currentResult = results[currentIndex];
  const previousUrl = currentIndex > 0 ? imageUrls[currentIndex - 1] : null;
  const nextUrl = currentIndex < total - 1 ? imageUrls[currentIndex + 1] : null;

  useEffect(() => {
    setCurrentIndex(0);
    setIsImageLoaded(false);
  }, [results]);

  const goToPrevious = useCallback(() => {
    if (isAtStart) {
      return;
    }

    setIsImageLoaded(false);
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, [isAtStart]);

  const goToNext = useCallback(() => {
    if (isAtEnd) {
      return;
    }

    setIsImageLoaded(false);
    setCurrentIndex((prev) => Math.min(total - 1, prev + 1));
  }, [isAtEnd, total]);

  useEffect(() => {
    if (total === 0) {
      return;
    }

    [previousUrl, nextUrl].filter(Boolean).forEach((url) => {
      const image = new Image();
      image.src = url;
    });
  }, [nextUrl, previousUrl, total]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        goToPrevious();
      }

      if (event.key === 'ArrowRight') {
        goToNext();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [goToNext, goToPrevious]);

  const openImageInBlackPage = () => {
    const win = window.open('', '_blank');
    if (!win || !mainUrl) {
      return;
    }

    const safeUrl = mainUrl.replace(/"/g, '&quot;');
    win.document.write(`<!doctype html><html><head><title>Image</title><meta name="viewport" content="width=device-width,initial-scale=1"/><style>html,body{height:100%}body{margin:0;display:flex;align-items:center;justify-content:center;background:#000}img{max-width:100%;max-height:100%;object-fit:contain;border-radius:10px}</style></head><body><img src="${safeUrl}" alt="Matched face"/></body></html>`);
    win.document.close();
  };

  const downloadCurrentImage = async (event) => {
    event.stopPropagation();

    const downloadUrl = downloadUrls[currentIndex];
    if (!downloadUrl || !currentResult?.img) {
      return;
    }

    const response = await fetch(downloadUrl);
    const contentType = response.headers.get('content-type') || '';

    if (!response.ok || contentType.includes('text/html')) {
      throw new Error('Image download failed');
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = currentResult.img;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  };

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches[0].clientX;
    touchDeltaXRef.current = 0;
  };

  const handleTouchMove = (event) => {
    touchDeltaXRef.current = event.touches[0].clientX - touchStartXRef.current;
  };

  const handleTouchEnd = () => {
    if (touchDeltaXRef.current > SWIPE_THRESHOLD) {
      goToPrevious();
    }

    if (touchDeltaXRef.current < -SWIPE_THRESHOLD) {
      goToNext();
    }

    touchStartXRef.current = 0;
    touchDeltaXRef.current = 0;
  };

  if (!results || results.length === 0) {
    return null;
  }

  return (
    <div className="results-carousel">
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        style={{ color: '#ffffff', fontWeight: 700, paddingBottom: '16px', fontSize: '1.1rem' }}
      >
        Matched Faces
      </Typography>

      <div
        style={{
          position: 'relative',
          width: '100vw',
          marginLeft: 'calc(50% - 50vw)',
          marginRight: 'calc(50% - 50vw)',
        }}
      >
        <div
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            position: 'relative',
            height: 'clamp(320px, 72vh, 860px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {previousUrl && (
            <SlidePreview url={previousUrl} direction="previous" />
          )}

          {nextUrl && (
            <SlidePreview url={nextUrl} direction="next" />
          )}

          <div
            style={{
              position: 'relative',
              zIndex: 2,
              transform: 'translateY(0) scale(1)',
              transition: 'transform .45s cubic-bezier(.22,.61,.36,1)',
            }}
            onClick={openImageInBlackPage}
          >
            <button
              type="button"
              title="Download"
              onClick={downloadCurrentImage}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: 'clamp(36px, 5vw, 44px)',
                height: 'clamp(36px, 5vw, 44px)',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.95)',
                border: '1px solid rgba(0,0,0,.06)',
                color: '#111',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 10px 22px rgba(0,0,0,.25)',
                textDecoration: 'none',
                zIndex: 4,
                cursor: 'pointer',
              }}
            >
              <i className="fa-solid fa-arrow-down" style={{ fontSize: 'clamp(14px, 2.5vw, 18px)' }} />
            </button>

            <div
              style={{
                width: 'clamp(300px, 60vw, 880px)',
                height: 'clamp(220px, 60vh, 560px)',
                background: '#0b0b0b',
                borderRadius: 20,
                overflow: 'hidden',
                boxShadow: '0 18px 50px rgba(0,0,0,.5)',
              }}
            >
              <img
                src={mainUrl}
                alt="Current"
                loading="lazy"
                onLoad={() => setIsImageLoaded(true)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  background: '#111',
                  opacity: isImageLoaded ? 1 : 0,
                  transition: 'opacity .3s ease',
                }}
              />
            </div>
          </div>

          <CarouselArrow
            direction="previous"
            disabled={isAtStart}
            onClick={goToPrevious}
          />
          <CarouselArrow
            direction="next"
            disabled={isAtEnd}
            onClick={goToNext}
          />
        </div>
      </div>
    </div>
  );
}

function SlidePreview({ url, direction }) {
  const isPrevious = direction === 'previous';

  return (
    <div
      style={{
        position: 'absolute',
        zIndex: 1,
        transform: `${isPrevious ? 'translateX(-26%)' : 'translateX(26%)'} translateY(6%) scale(0.82) rotate(${isPrevious ? '-1.2deg' : '1.2deg'})`,
        transition: 'transform .45s cubic-bezier(.22,.61,.36,1)',
        opacity: 0.9,
        filter: 'blur(0.3px)',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          width: 'clamp(260px, 52vw, 720px)',
          height: 'clamp(180px, 46vh, 480px)',
          background: '#0e0e0e',
          borderRadius: 10,
          overflow: 'hidden',
          boxShadow: '0 14px 40px rgba(0,0,0,.35)',
        }}
      >
        <img
          src={url}
          alt={isPrevious ? 'Previous' : 'Next'}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    </div>
  );
}

function CarouselArrow({ direction, disabled, onClick }) {
  const isPrevious = direction === 'previous';

  return (
    <button
      onClick={onClick}
      title={isPrevious ? 'Previous' : 'Next'}
      disabled={disabled}
      style={{
        position: 'absolute',
        [isPrevious ? 'left' : 'right']: 18,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 52,
        height: 52,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(0,0,0,.06)',
        color: '#111',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 10px 24px rgba(0,0,0,.3)',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'transform .15s ease',
        zIndex: 3,
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <i className={`fa-solid fa-chevron-${isPrevious ? 'left' : 'right'}`} />
    </button>
  );
}

export default ResultsGrid;
